import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, KeyRound, UserX, UserCheck, ShieldCheck, Send, Search, Copy, RefreshCw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PermissionMatrixEditor from '../components/PermissionMatrixEditor';
import DocumentScopesEditor from '../components/DocumentScopesEditor';
import UserAccessProfile from '../components/UserAccessProfile';
import CustomRolesManager from '../components/CustomRolesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  school_admin: 'School Administrator',
  head_of_operations: 'Head of Operations',
  school_bursar: 'School Bursar',
  project_manager: 'Project Manager',
  facility_manager: 'Facility Manager',
  registrar: 'Registrar',
  sponsorship_officer: 'Sponsorship Officer',
  finance_officer: 'Finance Officer',
  attendance_officer: 'Attendance Officer',
  hr_officer: 'HR Officer',
  volunteer_coordinator: 'Volunteer Coordinator',
  procurement_officer: 'Procurement Officer',
  teaching_staff: 'Teaching Staff',
  student: 'Student',
};

const ROLE_BADGE_COLORS = {
  super_admin: 'bg-red-100 text-red-800 border-red-200',
  school_admin: 'bg-purple-100 text-purple-800 border-purple-200',
  head_of_operations: 'bg-violet-100 text-violet-800 border-violet-200',
  school_bursar: 'bg-green-100 text-green-800 border-green-200',
  project_manager: 'bg-orange-100 text-orange-800 border-orange-200',
  facility_manager: 'bg-stone-100 text-stone-800 border-stone-200',
  registrar: 'bg-blue-100 text-blue-800 border-blue-200',
  sponsorship_officer: 'bg-pink-100 text-pink-800 border-pink-200',
  finance_officer: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  attendance_officer: 'bg-amber-100 text-amber-800 border-amber-200',
  hr_officer: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  volunteer_coordinator: 'bg-teal-100 text-teal-800 border-teal-200',
  procurement_officer: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  teaching_staff: 'bg-sky-100 text-sky-800 border-sky-200',
  student: 'bg-slate-100 text-slate-700 border-slate-200',
};

const randomPassword = () => {
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digit = '23456789';
  const special = '!@#$%^&*';
  const all = lower + upper + digit + special;
  let out = '';
  out += lower[Math.floor(Math.random() * lower.length)];
  out += upper[Math.floor(Math.random() * upper.length)];
  out += digit[Math.floor(Math.random() * digit.length)];
  out += special[Math.floor(Math.random() * special.length)];
  while (out.length < 12) out += all[Math.floor(Math.random() * all.length)];
  return out.split('').sort(() => Math.random() - 0.5).join('');
};

const AdminUsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);
  const [customRoles, setCustomRoles] = useState([]);

  const fetchCustomRoles = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/custom-roles`);
      setCustomRoles(r.data.items || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { fetchCustomRoles(); }, [fetchCustomRoles]);

  const roleLabel = (r) => ROLE_LABELS[r] || customRoles.find((c) => c.key === r)?.name || r;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (activeFilter !== 'all') params.is_active = activeFilter === 'active';
      const res = await axios.get(`${API_URL}/api/admin/users`, { params });
      setUsers(res.data || []);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, activeFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  if (user && user.role !== 'super_admin') {
    return (
      <div className="p-8 text-center">
        <ShieldCheck className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
        <p className="text-lg font-medium">Super Admin access required</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">User Management</h1>
          <p className="text-sm text-muted-foreground">Create users, assign roles, manage access.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-create-user-btn">
              <Plus className="h-4 w-4 mr-2" /> New User
            </Button>
          </DialogTrigger>
          <CreateUserDialog
            customRoles={customRoles}
            onCreated={(link) => {
              setCreateOpen(false);
              fetchUsers();
              if (link) setInviteLink(link);
            }}
          />
        </Dialog>
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-custom-roles">Custom Roles</TabsTrigger>
        </TabsList>
        <TabsContent value="roles" className="mt-4">
          <CustomRolesManager customRoles={customRoles} onChanged={fetchCustomRoles} />
        </TabsContent>
        <TabsContent value="users" className="mt-4 space-y-6">

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="admin-users-search"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="admin-role-filter">
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
              {customRoles.map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.name} (custom)</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={activeFilter} onValueChange={setActiveFilter}>
            <SelectTrigger data-testid="admin-active-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Users table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{users.length} user{users.length !== 1 && 's'}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead>Last login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={ROLE_BADGE_COLORS[u.role] || 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200'}>
                      {roleLabel(u.role)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1.5 flex-wrap">
                      {u.is_active ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-100 text-gray-700">Inactive</Badge>
                      )}
                      {u.invite_status === 'pending' && (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200">Invite pending</Badge>
                      )}
                      {u.must_change_password && u.invite_status !== 'pending' && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-800 border-orange-200">Must change pw</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <button className="text-xs underline decoration-dotted text-muted-foreground hover:text-foreground" onClick={() => setProfileUser(u)} data-testid={`admin-modules-${u.id}`}>
                      {u.role === 'super_admin' ? 'All modules' : `${Object.values(u.permissions_v2 || {}).filter((b) => b && b.view).length} modules`}
                    </button>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => setProfileUser(u)} title="Access profile" data-testid={`admin-profile-${u.id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingUser(u)} data-testid={`admin-edit-${u.id}`}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setResetUser(u)} data-testid={`admin-reset-${u.id}`}>
                      <KeyRound className="h-3.5 w-3.5" />
                    </Button>
                    {u.invite_status === 'pending' && (
                      <Button size="sm" variant="ghost" onClick={() => regenerateInvite(u.id, setInviteLink)} data-testid={`admin-regen-${u.id}`}>
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {u.is_active ? (
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(u, false, fetchUsers)} data-testid={`admin-deactivate-${u.id}`}>
                        <UserX className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(u, true, fetchUsers)} data-testid={`admin-activate-${u.id}`}>
                        <UserCheck className="h-3.5 w-3.5 text-green-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={!!editingUser} onOpenChange={(o) => !o && setEditingUser(null)}>
        {editingUser && (
          <EditUserDialog user={editingUser} customRoles={customRoles} onSaved={() => { setEditingUser(null); fetchUsers(); }} />
        )}
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        {resetUser && (
          <ResetPasswordDialog user={resetUser} onDone={() => { setResetUser(null); fetchUsers(); }} />
        )}
      </Dialog>

      {/* Access profile dialog */}
      <Dialog open={!!profileUser} onOpenChange={(o) => !o && setProfileUser(null)}>
        {profileUser && <UserAccessProfile user={profileUser} />}
      </Dialog>

      {/* Invite link display */}
      <Dialog open={!!inviteLink} onOpenChange={(o) => !o && setInviteLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invitation link generated</DialogTitle>
            <DialogDescription>
              Share this link with the user. It expires in 7 days.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted p-3 rounded text-xs break-all font-mono" data-testid="invite-link-display">
            {inviteLink}
          </div>
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success('Copied to clipboard'); }} data-testid="copy-invite-btn">
              <Copy className="h-4 w-4 mr-2" /> Copy link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Sub Components ============

const CreateUserDialog = ({ onCreated, customRoles = [] }) => {
  const [form, setForm] = useState({
    email: '', full_name: '', role: 'school_admin', phone: '',
    username: '', department: '', position: '', staff_id: '',
    creation_mode: 'set_password', temp_password: randomPassword(),
    must_change_password: false,
  });
  const [permissions, setPermissions] = useState({});
  const [documentScopes, setDocumentScopes] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, permissions_v2: permissions, document_scopes: documentScopes };
      if (payload.creation_mode === 'send_invite') delete payload.temp_password;
      const res = await axios.post(`${API_URL}/api/admin/users`, payload);
      toast.success('User created');
      onCreated(res.data.invite_link || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Create new user</DialogTitle>
        <DialogDescription>Assign a role and choose how the user gets initial access. Then customise the permission matrix.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <Tabs defaultValue="basic">
          <TabsList className="grid grid-cols-3 w-full max-w-xl">
            <TabsTrigger value="basic" data-testid="create-tab-basic">Basic Info</TabsTrigger>
            <TabsTrigger value="permissions" data-testid="create-tab-permissions">Permissions</TabsTrigger>
            <TabsTrigger value="documents" data-testid="create-tab-documents">Documents Access</TabsTrigger>
          </TabsList>
          <TabsContent value="basic" className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Full name</Label>
                <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="create-full-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                    {customRoles.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.name} (custom)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Picking a role auto-fills the permission matrix in the next tab.</p>
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="create-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username (optional)</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="create-username" />
              </div>
              <div>
                <Label>Employee / Staff ID (optional)</Label>
                <Input value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} data-testid="create-staff-id" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Department (optional)</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="create-department" />
              </div>
              <div>
                <Label>Position (optional)</Label>
                <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="create-position" />
              </div>
            </div>

            <Tabs value={form.creation_mode} onValueChange={(v) => setForm({ ...form, creation_mode: v })}>
              <TabsList className="grid grid-cols-2">
                <TabsTrigger value="set_password" data-testid="mode-set-pw">Set temp password</TabsTrigger>
                <TabsTrigger value="send_invite" data-testid="mode-send-invite">Send invitation link</TabsTrigger>
              </TabsList>
              <TabsContent value="set_password" className="space-y-2 pt-2">
                <Label>Temporary password</Label>
                <div className="flex gap-2">
                  <Input value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} required={form.creation_mode === 'set_password'} data-testid="create-temp-pw" />
                  <Button type="button" variant="outline" onClick={() => setForm({ ...form, temp_password: randomPassword() })} data-testid="gen-pw-btn">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">The user can log in with this password and keep using it — it does not expire. Must include uppercase, lowercase, digit, and special character — min 10 chars.</p>
                <label className="flex items-center gap-2 text-sm pt-1">
                  <input type="checkbox" checked={form.must_change_password} onChange={(e) => setForm({ ...form, must_change_password: e.target.checked })} data-testid="create-force-change" />
                  Require password change at first login (optional)
                </label>
              </TabsContent>
              <TabsContent value="send_invite" className="pt-2">
                <p className="text-sm">An invitation link will be generated. Copy and send to the user — they set their own password.</p>
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="permissions" className="pt-3">
            <PermissionMatrixEditor role={form.role} value={permissions} onChange={setPermissions} />
          </TabsContent>

          <TabsContent value="documents" className="pt-3">
            <DocumentScopesEditor role={form.role} value={documentScopes} onChange={setDocumentScopes} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="submit" disabled={submitting} data-testid="create-submit-btn">
            {submitting ? 'Creating…' : 'Create user'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};

const EditUserDialog = ({ user, onSaved, customRoles = [] }) => {
  const [form, setForm] = useState({
    full_name: user.full_name, phone: user.phone || '', role: user.role, is_active: user.is_active,
    username: user.username || '', department: user.department || '', position: user.position || '', staff_id: user.staff_id || '',
  });
  // Track the role the matrix was last filled FOR — so when role changes we
  // call the preset endpoint via PermissionMatrixEditor's autoFillOnRoleChange.
  // We initialise the matrix from the user's existing permissions_v2.
  const [permissions, setPermissions] = useState(user.permissions_v2 || {});
  const [documentScopes, setDocumentScopes] = useState(user.document_scopes || {});
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/admin/users/${user.id}`, {
        ...form, permissions_v2: permissions, document_scopes: documentScopes,
      });
      toast.success('User updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };
  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Edit user — {user.full_name}</DialogTitle>
        <DialogDescription>Update basic info and fine-tune the permission matrix.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <Tabs defaultValue="basic">
          <TabsList className="grid grid-cols-3 w-full max-w-xl">
            <TabsTrigger value="basic" data-testid="edit-tab-basic">Basic Info</TabsTrigger>
            <TabsTrigger value="permissions" data-testid="edit-tab-permissions">Permissions</TabsTrigger>
            <TabsTrigger value="documents" data-testid="edit-tab-documents">Documents Access</TabsTrigger>
          </TabsList>
          <TabsContent value="basic" className="space-y-3 pt-3">
            <div>
              <Label>Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="edit-full-name" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="edit-username" />
              </div>
              <div>
                <Label>Employee / Staff ID</Label>
                <Input value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} data-testid="edit-staff-id" />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="edit-department" />
              </div>
              <div>
                <Label>Position</Label>
                <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="edit-position" />
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                  {customRoles.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.name} (custom)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Changing the role re-applies that role's default permissions. You can override on the Permissions tab.</p>
            </div>
          </TabsContent>
          <TabsContent value="permissions" className="pt-3">
            <PermissionMatrixEditor role={form.role} value={permissions} onChange={setPermissions} />
          </TabsContent>
          <TabsContent value="documents" className="pt-3">
            <DocumentScopesEditor role={form.role} value={documentScopes} onChange={setDocumentScopes} />
          </TabsContent>
        </Tabs>
        <DialogFooter><Button type="submit" data-testid="edit-submit-btn">Save</Button></DialogFooter>
      </form>
    </DialogContent>
  );
};

const ResetPasswordDialog = ({ user, onDone }) => {
  const [pw, setPw] = useState(randomPassword());
  const [forceChange, setForceChange] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/users/${user.id}/reset-password`, {
        new_password: pw, force_change: forceChange,
      });
      toast.success('Password reset');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Reset password for {user.full_name}</DialogTitle>
        <DialogDescription>The user can log in immediately with the new password. Optionally require them to change it at next login.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>New password</Label>
          <div className="flex gap-2">
            <Input value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-pw-input" />
            <Button type="button" variant="outline" onClick={() => setPw(randomPassword())}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={forceChange} onChange={(e) => setForceChange(e.target.checked)} data-testid="reset-force-change" />
          Force user to change on next login
        </label>
        <DialogFooter><Button type="submit" data-testid="reset-submit-btn">Reset</Button></DialogFooter>
      </form>
    </DialogContent>
  );
};

// Action helpers
const toggleActive = async (user, activate, refetch) => {
  if (!window.confirm(`${activate ? 'Reactivate' : 'Deactivate'} ${user.full_name}?`)) return;
  try {
    if (activate) {
      await axios.post(`${API_URL}/api/admin/users/${user.id}/activate`);
    } else {
      await axios.delete(`${API_URL}/api/admin/users/${user.id}`);
    }
    toast.success('Done');
    refetch();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Failed');
  }
};

const regenerateInvite = async (userId, setInviteLink) => {
  try {
    const res = await axios.get(`${API_URL}/api/admin/users/${userId}/invite-link`);
    setInviteLink(res.data.invite_link);
  } catch (e) {
    toast.error('Failed to regenerate');
  }
};

export default AdminUsersPage;
